import { BaseKeepAlive } from '@nab/nab-x-sdk-core';
import { IKeepAliveOptions, IKeepAlive } from '@nab/x-types';
import { sdkLoggerInterface as log } from '../Logger/Logger';

/**
 * [[include:packages/consumer/sdk-browser/src/KeepAlive/README.md]]
 */
class KeepAlive extends BaseKeepAlive implements IKeepAlive {
    // Keeping this for backwards compatibility as not sure if this was used explicitly in pure JS functions
    private earlyCheckFactorForHeartbeat: number = 0.75;

    // Keeping this for backwards compatibility as not sure if this was used explicitly in pure JS functions
    private defaultEvents: string[] = ['mousemove', 'mousedown', 'keypress', 'touchmove'];

    /**
     * time out based on the idle timeout of the token
     */
    protected defaultOptions: IKeepAliveOptions = {
        idleTime: 120,
        earlyCheckFactorForHeartbeat: this.earlyCheckFactorForHeartbeat,
        events: this.defaultEvents,
        heartbeatProvider: (): Promise<boolean> => {
            return Promise.reject<boolean>(new Error('Heart beat function not passed in. Token will not be kept alive'));
        }
    };

    /**
     * gets the heartbeat interval id created by starting the Interval job for heart beat calls
     */
    public get HeartbeatIntervalId(): number {
        return this.heartbeatIntervalId;
    }

    /**
     * exposes the boolean that will inform where the user is active or not
     */
    public get IsUserActive(): boolean {
        return this.isUserActive;
    }

    /**
     * Exposes the default factor added to the time required for inactivity
     * This is required for the heart beat interval to be less that the inactivity timeout.
     */
    public get EarlyCheckFactorForHeartbeat(): number {
        const { earlyCheckFactorForHeartbeat } = this.options;
        return earlyCheckFactorForHeartbeat;
    }

    /**
     * Gets the inactivity timeout in milliseconds
     */
    public get InactiveTimeout(): number {
        return this.options.idleTime * 1000;
    }

    constructor(protected options: IKeepAliveOptions) {
        // We have to override the constructor as the overridden properties/variables like defaultOptions
        // gets read from this class and not base class
        super(options);
        this.options = { ...this.defaultOptions, ...options };
    }

    protected heartbeatCalls(): void {
        // eslint-disable-next-line no-underscore-dangle
        const appLoader = (window as any).__nabxAppLoader__;
        const { heartbeatProvider } = this.options;
        if (this.IsUserActive && appLoader && heartbeatProvider) {
            const tokens = appLoader.getCachedTokens() || [];
            tokens.forEach(async (token) => {
                try {
                    await heartbeatProvider(token);
                } catch (e) {
                    log.warn('unable to use token for the heart-beat call', e);
                }
            });
            this.inactivateUser();
        } else {
            this.destroy();
        }
    }

    protected startHeartBeatTimeOut(): void {
        const heartbeatIdleTimer = Math.round(this.InactiveTimeout * this.EarlyCheckFactorForHeartbeat);
        // had to bind the this here as we are following inheritance and use of arrow functions cause reference issues.
        // But the event handler works on the container context and the this gets overridden
        this.heartbeatIntervalId = window.setInterval(this.heartbeatCalls.bind(this), heartbeatIdleTimer);
    }

    protected setupEvents(): void {
        const { container = window, events } = this.options;
        events.forEach((event: string) => {
            // had to bind the this here as we are following inheritance and use of arrow functions cause reference issues.
            // But the event handler works on the container context and the this gets overridden
            container.addEventListener(event, this.keepUserActive.bind(this), false);
        });
    }

    protected destroyEvents(): void {
        const { container = window, events } = this.options;
        events.forEach((event: string) => {
            // had to bind the this here as we are following inheritance and use of arrow functions cause reference issues.
            // But the event handler works on the container context and the this gets overridden
            container.removeEventListener(event, this.keepUserActive.bind(this), false);
        });
    }
}

export { KeepAlive };
